<template>
  <div class="multipart-uploader" :class="dynamicClass">
    <template v-if="untouched || deleted">
      <el-tooltip
        v-if="!disabled"
        :content="tooltipContent"
        :open-delay="500"
        :visible-arrow="false">
        <input
          type="file"
          :accept="acceptAttr"
          @input="handleChange"
          @dragover="dragover = true"
          @dragleave="dragover = false"
          @drop="dragover = false">
      </el-tooltip>
      <div class="uploader-empty-dummy" :class="{ disabled }">
        <template v-if="disabled">
          <el-icon>
            <WarningFilled/>
          </el-icon>
          <slot name="disabled-empty-dummy">
            Программист еще не добавлял ZIP файл с исходными кодами
          </slot>
        </template>
        <template v-else>
          <slot name="trigger-icon">
            <el-icon>
              <UploadFilled/>
            </el-icon>
          </slot>
          <span class="trigger-text">
            <slot name="trigger-text">
              Добавьте файл с исходными кодами
            </slot>
          </span>
        </template>
      </div>
    </template>
    <div v-if="showFile" class="file-data">
      <el-tooltip
        :content="fileName"
        placement="top"
        :open-delay="500"
        :visible-arrow="false">
        <div class="file-name">
          {{ fileName }}
        </div>
      </el-tooltip>
      <img src="/static/img/fileIcon.svg" alt="">
      <div v-if="fileType" class="file-format">
        {{ fileType }}
      </div>
      <div v-if="!init" class="file-status-icon">
        <el-icon :class="[fileStatusIcon, {'is-loading': fileStatusIcon === 'Loading'}]">
          <component :is="fileStatusIcon"/>
        </el-icon>
      </div>
      <template v-if="loading || checking">
        <div class="file-progress">
          <span v-if="loading">{{ displayedPercent }}%</span>
          <span v-else-if="checking">Проверка...</span>
        </div>
        <p-progress v-if="loading" :value="displayedPercent" class="file-progress-bar"/>
        <el-icon v-if="loading" class="cancel-load-icon" @click="handleAbortBtnClick">
          <CircleClose/>
        </el-icon>
      </template>
    </div>
    <div
      v-if="showToolbar"
      class="file-toolbar"
      @mouseenter="disableTooltips = false"
      @mouseleave="disableTooltips = true">
      <el-icon class="el-icon-more">
        <More/>
      </el-icon>
      <template v-if="showViewToolbar">
        <el-tooltip
          :disabled="disableTooltips"
          :content="$t('Download file')"
          :open-delay="200">
          <a class="file-toolbar-btn" :href="downloadLink" download>
            <el-icon>
              <Download/>
            </el-icon>
          </a>
        </el-tooltip>
        <el-tooltip
          v-if="viewBtns.copy"
          :disabled="disableTooltips"
          :content="$t('Copy link')"
          :open-delay="200">
          <el-icon class="file-toolbar-btn" @click="handleCopyBtnClick">
            <CopyDocument/>
          </el-icon>
        </el-tooltip>
        <el-tooltip
          v-if="viewBtns.archive"
          :disabled="disableTooltips"
          :content="$t('Show archive')">
          <i class="fa fa-folder-open-o file-toolbar-btn" @click="handleArchiveBtnClick"/>
        </el-tooltip>
      </template>
      <template v-if="showEditToolbar">
        <el-tooltip
          v-if="failed || checkFailed"
          :disabled="disableTooltips"
          :content="$t('Retry')"
          :open-delay="200">
          <el-icon class="file-toolbar-btn" @click="handleRetryBtnClick">
            <Refresh/>
          </el-icon>
        </el-tooltip>
        <el-tooltip
          :disabled="disableTooltips"
          :content="$t('Remove file')"
          :open-delay="200">
          <el-icon class="file-toolbar-btn" @click="handleAbortBtnClick">
            <Close/>
          </el-icon>
        </el-tooltip>
      </template>
    </div>

    <el-dialog
      v-model="showArchiveModal"
      title="Структура архива"
      width="50%"
      class="file-archive-modal custom-scroll"
      append-to-body
      @close="handleCloseArchive">
      <el-tree
        v-if="showArchiveModal"
        ref="tree"
        v-loading="archiveLoading"
        :load="loadArchive"
        :props="defaultProps"
        :empty-text="archiveLoading ? '' : emptyText"
        :icon="icon"
        lazy
        class="custom-loading-svg"
        element-loading-text="Loading..."
        :element-loading-spinner="iconLoading"
        element-loading-svg-view-box="0, 0, 500, 500"
        element-loading-background="#fff">
        <template #default="{node}">
          <el-tooltip
            :disabled="node.label.length < 55"
            :content="node.label"
            placement="top"
            :open-delay="500"
            :visible-arrow="false">
            <span class="el-tree-node__label">{{ node.label }}</span>
          </el-tooltip>
        </template>
      </el-tree>
    </el-dialog>
    <check-file-archive
      :loading="archiveLoading"
      :archive-model="archiveModel"
      :close-dialog="handleCloseCheckArchive"/>
  </div>
</template>

<script>
import { ElTooltip, ElTree, ElDialog } from 'element-plus'
import '@/assets/sass/style/filesMultipartUploader.scss'
import { addFirstSlash, checkFileFormat, checkFileSize, getFileUrl } from '@/utils/helpers'
import PProgress from '@/components/UIComponents/Progress'
import CheckFileArchive from '@/components/UIComponents/Custom/CheckFileArchive.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import Load from '@/utils/helperClasses/Load'
import { Folder } from '@element-plus/icons-vue'
import { ICON_LOADING_MARKUP } from '@/utils/constants'

export default {
  name: 'FilesMultipartUploader',
  components: {
    PProgress,
    ElTooltip,
    ElTree,
    ElDialog,
    CheckFileArchive
  },
  props: {
    handler: Function,
    afterUploading: Function,
    beforeUploading: Function,
    initUrl: String,
    disabled: {
      type: Boolean,
      default: false
    },
    mixEditAndView: {
      type: Boolean,
      default: false
    },
    responsive: {
      type: Boolean,
      default: false
    },
    availableTypes: {
      type: Array,
      default: () => (['zip'])
    },
    sizeLimit: {
      type: Number,
      default: 0
    },
    viewBtns: {
      type: Object,
      default: () => ({
        copy: true,
        archive: true
      })
    },
    tooltipContent: {
      type: String,
      default: 'Один файл в формате ZIP'
    }
  },
  data: () => ({
    /*
    Значения поля status:
      'untouched' - начальное состояние без исходного файла
      'init' - начальное состояние с исходным файлом
      'loading' - загрузка файла
      'checking' - файл успешно загружен, идет проверка
      'success' - файл успешно загружен и валидный
      'failed' - загрузка не удалась
      'checkFailed' - проверка не удалась
      'invalid' - файл невалидный
      'deleted' - файл был удален
    */
    status: undefined,
    uid: undefined,
    file: undefined,
    loader: undefined,
    url: undefined,
    percent: 0,
    displayedPercent: 0,
    dragover: false,
    disableTooltips: true,
    showArchiveModal: false,
    archiveLoading: false,
    defaultProps: {
      children: 'children',
      label: 'label',
      isLeaf: 'leaf'
    },
    archiveModel: {
      title: '',
      files: []
    },
    icon: Folder,
    iconLoading: ICON_LOADING_MARKUP
  }),
  computed: {
    ...mapGetters(['archive']),
    untouched() {
      return this.status === 'untouched'
    },
    init() {
      return this.status === 'init'
    },
    loading() {
      return this.status === 'loading'
    },
    checking() {
      return this.status === 'checking'
    },
    success() {
      return this.status === 'success'
    },
    failed() {
      return this.status === 'failed'
    },
    checkFailed() {
      return this.status === 'checkFailed'
    },
    invalid() {
      return this.status === 'invalid'
    },
    deleted() {
      return this.status === 'deleted'
    },
    dynamicClass() {
      const list = [`status-${this.status}`]
      if (this.dragover) list.push('dragover')
      if (this.disabled) list.push('disabled-input')
      if (this.responsive) list.push('responsive')
      return list.join(' ')
    },
    showFile() {
      return [
        this.init,
        this.loading,
        this.checking,
        this.success,
        this.failed,
        this.checkFailed,
        this.invalid
      ].some(i => i)
    },
    fileName() {
      if (this.file) {
        return String(this.file.name)
      }
      const getName = () => {
        const parts = this.initUrl?.split('/')
        return parts[parts.length - 1]
      }
      return this.initUrl ? getName() : undefined
    },
    fileType() {
      if (!this.fileName) return
      return this.fileName.split('.').reverse()[0]
    },
    fileStatusIcon() {
      return {
        loading: 'Loading',
        checking: 'Loading',
        success: 'SuccessFilled',
        failed: 'Warning',
        checkFailed: 'Warning',
        invalid: 'Warning'
      }[this.status]
    },
    showEditToolbar() {
      if (this.disabled || !this.hasUrl) return false
      return this.init || this.failed || this.checkFailed || this.invalid || this.success
    },
    showViewToolbar() {
      if (!this.hasUrl) return false
      return this.disabled || (this.init && this.mixEditAndView)
    },
    showToolbar() {
      return this.showViewToolbar || this.showEditToolbar
    },
    downloadLink() {
      return '/cdn' + addFirstSlash(this.url || this.initUrl)
    },
    acceptAttr() {
      return '.' + this.availableTypes.join(',.').toLowerCase()
    },
    hasUrl() {
      return !!this.url || !!this.initUrl
    },
    emptyText() {
      return this.archive ? 'Не получилось загрузить архив' : 'Архив формируется. Попробуйте позже.'
    }
  },
  watch: {
    percent() {
      this.changePercent()
    },
    success(value) {
      if (!value) return
      this.handler([
        {
          url: this.url,
          status: 'success'
        }
      ])
    },
    failed(value) {
      if (!value) return
      this.handler([])
    },
    checkFailed(value) {
      if (!value) return
      this.handler([])
    },
    deleted(value) {
      if (!value) return
      this.handler([])
    }
  },
  methods: {
    ...mapMutations(['setArchive']),
    ...mapActions(['downloadArchive']),
    async handleChange(event) {
      const file = event.target.files[0]
      if (this.availableTypes.length) {
        const { formatValid, errMsg } = checkFileFormat(file, this.availableTypes)
        if (!formatValid) {
          this.$notifyError(errMsg)
          return
        }
      }
      this.uid = Date.now()
      this.status = 'loading'
      let resultClientCheck = []
      this.file = file
      if (this.beforeUploading) {
        this.archiveLoading = true
        this.archiveModel.title = file.name
        resultClientCheck = await this.beforeUploading(file)
        this.archiveLoading = false
      }
      if (resultClientCheck.length) {
        this.archiveModel.files = resultClientCheck
        this.status = 'invalid'
        this.url = 'error'
        return
      }
      if (this.sizeLimit) {
        const { sizeValid, errMsg } = checkFileSize(file, this.sizeLimit)
        if (errMsg) this.$notifyError(errMsg)
        if (!sizeValid) {
          this.status = 'invalid'
          this.url = 'error'
          return
        }
      }
      this.$emit('begin-uploading', this.uid)
      this.loader = new Load(
        event.target.files,
        this.handleSuccess,
        this.handleChunkLoad,
        this.handleError
      )
      this.loader.apply()
    },
    async handleSuccess(url) {
      this.url = url
      this.loader = undefined
      if (this.afterUploading) {
        this.status = 'checking'
        this.status = await this.afterUploading(url)
      } else {
        this.status = 'success'
      }
      this.$emit('end-uploading', this.uid)
    },
    handleError() {
      this.status = 'failed'
      this.url = 'error'
      this.$emit('end-uploading', this.uid)
    },
    handleChunkLoad(percent) {
      this.percent = percent
    },
    changePercent() {
      if (!this.percent) {
        this.displayedPercent = 0
        return
      }
      const id = setInterval(() => {
        if (this.displayedPercent === this.percent) {
          clearInterval(id)
          return
        }
        this.displayedPercent++
      }, 20)
    },
    async handleRetryBtnClick() {
      if (this.failed) {
        this.status = 'loading'
        this.loader.retry()
      } else if (this.checkFailed) {
        this.status = 'checking'
        this.status = await this.afterUploading(this.url)
      }
    },
    async handleAbortBtnClick() {
      this.percent = 0
      this.url = undefined
      this.status = 'deleted'
      if (this.loader) {
        await this.loader.abort()
        this.loader = undefined
      }
    },
    async handleCopyBtnClick() {
      const url = decodeURIComponent(getFileUrl(this.initUrl))
      await navigator.clipboard.writeText(url)
      this.$notifyInfo('Link copied')
    },
    handleArchiveBtnClick() {
      this.archiveLoading = true
      this.showArchiveModal = true
    },
    handleCloseArchive() {
      this.setArchive([])
    },
    async loadArchive(node, resolve) {
      if (node.level === 0) {
        await this.downloadArchive('')
        this.archiveLoading = false
        if (!this.archive) return
        return resolve(this.archive)
      }
      const fullPath = this.getFullPath(node, node.data.label)
      await this.downloadArchive(fullPath)
      resolve(this.archive)
      if (!this.archive.length) {
        this.$nextTick(() => {
          node.isLeaf = false
          node.expanded = false
          node.data.label = `${node.data.label} (${this.$t('Empty')})`
        })
      }
    },
    getFullPath(node, fullPath) {
      if (node.level === 1) {
        return fullPath
      }
      const path = `${node.parent.data.label}/${fullPath}`
      return this.getFullPath(node.parent, path)
    },
    handleCloseCheckArchive() {
      this.archiveModel = {
        title: '',
        files: []
      }
    },
    reset() {
      this.url = undefined
      this.file = undefined
      this.status = 'init'
    }
  },
  beforeMount() {
    this.status = this.initUrl ? 'init' : 'untouched'
  },
  beforeUnmount() {
    this.loader?.abort()
  }
}
</script>

<style scoped>

</style>
